import { useEffect, useMemo, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  ArrowUpRight,
  CheckCircle2,
  ChevronDown,
  ClipboardCheck,
  Eye,
  FileText,
  Inbox,
  Mail,
  MapPin,
  MessageSquare,
  Phone,
  Search,
  Send,
  ShieldAlert,
  Sparkles,
  ThumbsDown,
  ThumbsUp,
  XCircle,
} from 'lucide-react';
import { Avatar } from '../../components/Avatar';
import { Badge } from '../../components/Badge';
import { Banner } from '../../components/Banner';
import { Button } from '../../components/Button';
import { Card } from '../../components/Card';
import { Drawer } from '../../components/Drawer';
import { EmptyState } from '../../components/EmptyState';
import { Input } from '../../components/Input';
import { Modal } from '../../components/Modal';
import { Money } from '../../components/Money';
import { PageHeader } from '../../components/PageHeader';
import { cn } from '../../lib/cn';
import { formatDate, timeAgo } from '../../lib/format';
import { useResellerApplications, decideResellerApplication } from '../../api/admin';
import type {
  ResellerApplication,
  ResellerApplicationStatus,
} from '../../mocks/admin';

/* ──────────────────────────────────────────────────────────────
 * Status presentation
 * ────────────────────────────────────────────────────────────── */

const STATUS_LABEL: Record<ResellerApplicationStatus, string> = {
  pending: 'Pending',
  in_review: 'In review',
  more_info_needed: 'Needs info',
  approved: 'Approved',
  rejected: 'Rejected',
  withdrawn: 'Withdrawn',
};

const STATUS_TONE: Record<ResellerApplicationStatus, 'success' | 'warning' | 'error' | 'info' | 'neutral'> = {
  pending: 'warning',
  in_review: 'info',
  more_info_needed: 'warning',
  approved: 'success',
  rejected: 'error',
  withdrawn: 'neutral',
};

const ACTIONABLE_STATUSES: ReadonlyArray<ResellerApplicationStatus> = [
  'pending',
  'in_review',
  'more_info_needed',
];

/* Country code → label (only the ones in mock data; fall back to code). */
const COUNTRY_LABEL: Record<string, string> = {
  BD: 'Bangladesh',
  IN: 'India',
  PK: 'Pakistan',
  XX: 'Unknown',
};

function countryName(code: string): string {
  return COUNTRY_LABEL[code] ?? code;
}

/* ──────────────────────────────────────────────────────────────
 * Page
 * ────────────────────────────────────────────────────────────── */

/** Pending reseller applications — list + detail drawer + decision sub-drawers. */
export function ResellerApplications() {
  const { data: initialApplications } = useResellerApplications();
  const [applications, setApplications] = useState<ResellerApplication[]>([]);
  const [statusFilter, setStatusFilter] = useState<'all' | ResellerApplicationStatus>('all');
  const [countryFilter, setCountryFilter] = useState<string>('all');
  const [search, setSearch] = useState('');
  const [openId, setOpenId] = useState<string | null>(null);

  // Seed local (mutable) state once the fetched applications land. Keyed on the
  // raw query data (stable `undefined` until loaded) so this runs once.
  useEffect(() => {
    if (!initialApplications) return;
    setApplications(initialApplications);
  }, [initialApplications]);

  /* KPI counts — computed off the live (mutated) state, not the imported array. */
  const kpis = useMemo(() => {
    const monthStart = new Date();
    monthStart.setDate(1);
    monthStart.setHours(0, 0, 0, 0);
    return {
      pending: applications.filter((a) => a.status === 'pending').length,
      inReview: applications.filter((a) => a.status === 'in_review').length,
      needsInfo: applications.filter((a) => a.status === 'more_info_needed').length,
      approvedThisMonth: applications.filter(
        (a) => a.status === 'approved' && a.reviewedAt && new Date(a.reviewedAt) >= monthStart,
      ).length,
    };
  }, [applications]);

  /* Distinct country list for the filter dropdown. */
  const countries = useMemo(() => {
    const set = new Set(applications.map((a) => a.country));
    return Array.from(set).sort();
  }, [applications]);

  const filtered = useMemo(() => {
    return applications.filter((a) => {
      if (statusFilter !== 'all' && a.status !== statusFilter) return false;
      if (countryFilter !== 'all' && a.country !== countryFilter) return false;
      if (search) {
        const s = search.toLowerCase();
        const haystack = `${a.orgName} ${a.email} ${a.applicantName}`.toLowerCase();
        if (!haystack.includes(s)) return false;
      }
      return true;
    });
  }, [applications, statusFilter, countryFilter, search]);

  /* Sort: actionable first (newest), then everything else (newest first). */
  const sorted = useMemo(() => {
    const ranked = (s: ResellerApplicationStatus) =>
      ACTIONABLE_STATUSES.includes(s) ? 0 : 1;
    return [...filtered].sort((a, b) => {
      const r = ranked(a.status) - ranked(b.status);
      if (r !== 0) return r;
      return new Date(b.submittedAt).getTime() - new Date(a.submittedAt).getTime();
    });
  }, [filtered]);

  const open = openId ? applications.find((a) => a.id === openId) ?? null : null;

  /* Mutators — passed to the drawer for in-place state transitions. */
  function patch(id: string, patcher: (a: ResellerApplication) => ResellerApplication) {
    setApplications((arr) => arr.map((a) => (a.id === id ? patcher(a) : a)));
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 sm:py-8 max-w-[1440px] mx-auto">
      <Link
        to="/admin/resellers"
        className="inline-flex items-center gap-1.5 text-sm text-zinc-500 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100 mb-4 transition"
      >
        <ArrowLeft className="w-4 h-4" />
        All resellers
      </Link>

      <PageHeader
        eyebrow="Platform"
        title={
          <span>
            Reseller applications{' '}
            <span className="text-zinc-400 dark:text-zinc-500 font-medium tabular-nums">
              ({applications.length})
            </span>
          </span>
        }
        subtitle="Review and approve / reject incoming partner applications."
      />

      {/* KPI tiles */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6">
        <KpiTile label="Pending" value={kpis.pending} icon={<Inbox className="w-4 h-4" />} tone="warning" />
        <KpiTile label="In review" value={kpis.inReview} icon={<Eye className="w-4 h-4" />} tone="info" />
        <KpiTile label="Needs info" value={kpis.needsInfo} icon={<MessageSquare className="w-4 h-4" />} tone="warning" />
        <KpiTile label="Approved this month" value={kpis.approvedThisMonth} icon={<CheckCircle2 className="w-4 h-4" />} tone="success" />
      </div>

      <Card className="overflow-hidden">
        {/* Filter bar */}
        <div className="px-5 sm:px-6 py-3 flex flex-wrap items-center gap-2 border-b border-zinc-200 dark:border-zinc-700">
          <FilterChip
            label="Status"
            value={statusFilter === 'all' ? 'All' : STATUS_LABEL[statusFilter]}
            options={[
              { v: 'all', l: 'All' },
              { v: 'pending', l: 'Pending' },
              { v: 'in_review', l: 'In review' },
              { v: 'more_info_needed', l: 'Needs info' },
              { v: 'approved', l: 'Approved' },
              { v: 'rejected', l: 'Rejected' },
              { v: 'withdrawn', l: 'Withdrawn' },
            ]}
            onChange={(v) => setStatusFilter(v as typeof statusFilter)}
          />
          <FilterChip
            label="Country"
            value={countryFilter === 'all' ? 'All' : countryName(countryFilter)}
            options={[
              { v: 'all', l: 'All countries' },
              ...countries.map((c) => ({ v: c, l: countryName(c) })),
            ]}
            onChange={setCountryFilter}
          />
          <div className="ml-auto w-full sm:w-72">
            <Input
              placeholder="Search by org name or email…"
              prefix={<Search className="w-4 h-4" />}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>

        {/* Table */}
        {sorted.length === 0 ? (
          <EmptyState
            icon={<Inbox className="w-5 h-5" />}
            title="No applications match your filters"
            description="Try adjusting the status or country filter, or clearing the search."
            action={
              <Button
                variant="secondary"
                onClick={() => {
                  setStatusFilter('all');
                  setCountryFilter('all');
                  setSearch('');
                }}
              >
                Clear filters
              </Button>
            }
          />
        ) : (
          <div className="overflow-x-auto nice-scroll">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase tracking-wide text-zinc-500 dark:text-zinc-400 bg-zinc-50/50 dark:bg-zinc-900/50">
                <tr>
                  <th className="text-left font-medium px-5 sm:px-6 py-3">Applicant</th>
                  <th className="text-left font-medium px-4 py-3">Organization</th>
                  <th className="text-left font-medium px-4 py-3 hidden md:table-cell">Country</th>
                  <th className="text-left font-medium px-4 py-3">Submitted</th>
                  <th className="text-left font-medium px-4 py-3">Status</th>
                  <th className="px-5 sm:px-6 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-200 dark:divide-zinc-700">
                {sorted.map((a) => (
                  <tr
                    key={a.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => setOpenId(a.id)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setOpenId(a.id);
                      }
                    }}
                    className="group cursor-pointer hover:bg-zinc-50 dark:hover:bg-zinc-800/70 focus:bg-zinc-50 dark:focus:bg-zinc-800/70 focus:outline-none transition"
                  >
                    <td className="px-5 sm:px-6 py-4">
                      <div className="flex items-center gap-3 min-w-0">
                        <Avatar name={a.applicantName} size="md" />
                        <div className="min-w-0">
                          <div className="font-medium text-zinc-950 dark:text-zinc-50 group-hover:text-brand dark:group-hover:text-brand-dm transition truncate">
                            {a.applicantName}
                          </div>
                          <div className="text-xs text-zinc-500 dark:text-zinc-400 truncate mono">{a.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <div className="font-medium text-zinc-900 dark:text-zinc-100 truncate max-w-[14rem]">
                        {a.orgName}
                      </div>
                      <div className="text-xs text-zinc-500 dark:text-zinc-400 tabular-nums">
                        Expected{' '}
                        <Money minor={a.expectedVolumeMinor} hideCents tone="neutral" />
                        /mo
                      </div>
                    </td>
                    <td className="px-4 py-4 hidden md:table-cell text-zinc-700 dark:text-zinc-200">
                      <span className="inline-flex items-center gap-1.5">
                        <MapPin className="w-3.5 h-3.5 text-zinc-400" />
                        {countryName(a.country)}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-zinc-700 dark:text-zinc-200 tabular-nums">
                      <div>{timeAgo(a.submittedAt)}</div>
                      <div className="text-[11px] text-zinc-500 dark:text-zinc-400">
                        {formatDate(a.submittedAt, { compact: true })}
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <Badge tone={STATUS_TONE[a.status]}>{STATUS_LABEL[a.status]}</Badge>
                    </td>
                    <td className="px-5 sm:px-6 py-4 text-right">
                      <span className="opacity-0 group-hover:opacity-100 group-focus:opacity-100 transition">
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            setOpenId(a.id);
                          }}
                          iconRight={<ArrowUpRight className="w-3.5 h-3.5" />}
                        >
                          Review
                        </Button>
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="px-5 sm:px-6 py-3 border-t border-zinc-200 dark:border-zinc-700 text-xs text-zinc-500 dark:text-zinc-400 flex items-center justify-between">
          <span>
            Showing <span className="tabular-nums">{sorted.length}</span> of{' '}
            <span className="tabular-nums">{applications.length}</span>
          </span>
          <span className="hidden sm:inline">Anyone can apply at /become-a-reseller. New ones land here.</span>
        </div>
      </Card>

      {open && (
        <ApplicationDrawer
          application={open}
          onClose={() => setOpenId(null)}
          onPatch={(p) => patch(open.id, p)}
        />
      )}
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────
 * Filter chip (matches AuditLog convention)
 * ────────────────────────────────────────────────────────────── */

function FilterChip({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: Array<{ v: string; l: string }>;
  onChange: (v: string) => void;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium bg-white dark:bg-zinc-900 text-zinc-700 dark:text-zinc-200 border border-zinc-200 dark:border-zinc-700 hover:bg-zinc-50 dark:hover:bg-zinc-800 transition"
      >
        <span className="text-zinc-500 dark:text-zinc-400">{label}:</span>
        <span>{value}</span>
        <ChevronDown className="w-3 h-3 text-zinc-400" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute top-full mt-1 left-0 z-20 min-w-[180px] rounded-md border border-zinc-200 dark:border-zinc-600 bg-white dark:bg-zinc-900 shadow-md py-1 max-h-72 overflow-y-auto nice-scroll">
            {options.map((o) => (
              <button
                key={o.v}
                type="button"
                onClick={() => {
                  onChange(o.v);
                  setOpen(false);
                }}
                className="block w-full text-left px-3 py-1.5 text-xs hover:bg-zinc-50 dark:hover:bg-zinc-800 text-zinc-700 dark:text-zinc-200"
              >
                {o.l}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────
 * KPI tile
 * ────────────────────────────────────────────────────────────── */

function KpiTile({
  label,
  value,
  icon,
  tone,
}: {
  label: string;
  value: number;
  icon: React.ReactNode;
  tone: 'info' | 'warning' | 'success' | 'neutral';
}) {
  const palette = {
    info: 'text-sky-700 dark:text-sky-300 bg-sky-50 dark:bg-sky-950/40',
    warning: 'text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-950/40',
    success: 'text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-950/40',
    neutral: 'text-zinc-700 dark:text-zinc-200 bg-zinc-100 dark:bg-zinc-800',
  }[tone];
  return (
    <Card className="p-4 sm:p-5">
      <div className="flex items-center gap-2 mb-2">
        <span className={cn('w-7 h-7 grid place-items-center rounded-md', palette)}>{icon}</span>
        <span className="text-xs font-medium uppercase tracking-wider text-zinc-500 dark:text-zinc-400">{label}</span>
      </div>
      <div className="text-2xl sm:text-3xl font-semibold text-zinc-950 dark:text-zinc-50 tabular-nums">{value}</div>
    </Card>
  );
}

/* ──────────────────────────────────────────────────────────────
 * Application drawer
 * ────────────────────────────────────────────────────────────── */

type SubFlow = 'approve' | 'reject' | 'request_info' | null;

function ApplicationDrawer({
  application,
  onClose,
  onPatch,
}: {
  application: ResellerApplication;
  onClose: () => void;
  onPatch: (patcher: (a: ResellerApplication) => ResellerApplication) => void;
}) {
  const [flow, setFlow] = useState<SubFlow>(null);
  const [reverseConfirm, setReverseConfirm] = useState(false);
  const [internalNoteDraft, setInternalNoteDraft] = useState('');
  const [noteSaved, setNoteSaved] = useState(false);
  const qc = useQueryClient();
  const isActionable = ACTIONABLE_STATUSES.includes(application.status);

  function addTimelineEntry(
    a: ResellerApplication,
    kind: ResellerApplication['timeline'][number]['kind'],
    text: string,
    by = 'admin@itcitysoft.com',
  ): ResellerApplication {
    return {
      ...a,
      timeline: [
        ...a.timeline,
        { at: new Date().toISOString(), by, kind, text },
      ],
    };
  }

  function markInReview() {
    onPatch((a) =>
      addTimelineEntry(
        {
          ...a,
          status: 'in_review',
          reviewedBy: 'admin@itcitysoft.com',
          reviewedAt: new Date().toISOString(),
        },
        'status_change',
        'Marked as in_review.',
      ),
    );
  }

  function saveInternalNote() {
    if (!internalNoteDraft.trim()) return;
    const text = internalNoteDraft.trim();
    onPatch((a) =>
      addTimelineEntry(
        {
          ...a,
          internalNotes: a.internalNotes ? `${a.internalNotes}\n\n${text}` : text,
        },
        'note',
        text,
      ),
    );
    setInternalNoteDraft('');
    setNoteSaved(true);
    setTimeout(() => setNoteSaved(false), 2200);
  }

  function reverseDecision() {
    onPatch((a) =>
      addTimelineEntry(
        {
          ...a,
          status: 'in_review',
          decisionReason: undefined,
          reapplyAfter: undefined,
        },
        'status_change',
        'Decision reversed — moved back to in_review.',
      ),
    );
    setReverseConfirm(false);
  }

  /* Sub-flow: render replaces drawer content (not a sub-drawer) per spec note. */
  if (flow === 'approve') {
    return (
      <ApproveDrawer
        application={application}
        onBack={() => setFlow(null)}
        onClose={onClose}
        onApproved={(commissionPct, sentInvite, note) => {
          decideResellerApplication(application.id, {
            decision: 'approve',
            commissionPct,
            note: note.trim() || undefined,
          })
            .then(() => qc.invalidateQueries({ queryKey: ['admin', 'applications', 'count'] }))
            .catch((e) => window.alert(`Approval failed to save: ${e?.message ?? e}`));
          onPatch((a) => {
            const newId = `rsl_new_${a.id.replace('app_', '')}`;
            const inviteText = sentInvite ? 'Invite email sent.' : 'No invite sent (will send later).';
            let next: ResellerApplication = {
              ...a,
              status: 'approved',
              reviewedBy: 'admin@itcitysoft.com',
              reviewedAt: new Date().toISOString(),
              decisionReason: `Approved at ${commissionPct}% commission. ${inviteText}`,
              resellerId: newId,
            };
            if (note.trim()) {
              next = addTimelineEntry(next, 'note', note.trim());
            }
            return addTimelineEntry(
              next,
              'status_change',
              `Approved. Reseller ${newId} created. ${inviteText}`,
            );
          });
        }}
      />
    );
  }
  if (flow === 'reject') {
    return (
      <RejectDrawer
        application={application}
        onBack={() => setFlow(null)}
        onClose={onClose}
        onRejected={(reason, message, reapply) => {
          decideResellerApplication(application.id, { decision: 'reject', note: reason })
            .then(() => qc.invalidateQueries({ queryKey: ['admin', 'applications', 'count'] }))
            .catch((e) => window.alert(`Rejection failed to save: ${e?.message ?? e}`));
          const reapplyAfter =
            reapply === 'none'
              ? undefined
              : new Date(Date.now() + (reapply === '30' ? 30 : 90) * 86400000)
                  .toISOString()
                  .slice(0, 10);
          onPatch((a) => {
            let next: ResellerApplication = {
              ...a,
              status: 'rejected',
              reviewedBy: 'admin@itcitysoft.com',
              reviewedAt: new Date().toISOString(),
              decisionReason: reason,
              reapplyAfter,
            };
            if (message.trim()) {
              next = addTimelineEntry(next, 'reply', `Sent to applicant: ${message.trim()}`);
            }
            const reapplyLabel =
              reapply === 'none' ? 'No re-apply.' : `Reapply window: ${reapply} days.`;
            return addTimelineEntry(
              next,
              'status_change',
              `Rejected. ${reapplyLabel}`,
            );
          });
        }}
      />
    );
  }
  if (flow === 'request_info') {
    return (
      <RequestInfoDrawer
        application={application}
        onBack={() => setFlow(null)}
        onClose={onClose}
        onSent={(asks, message) => {
          onPatch((a) => {
            const formatted = asks.length > 0 ? asks.join(', ') : 'additional details';
            let next: ResellerApplication = {
              ...a,
              status: 'more_info_needed',
              reviewedBy: 'admin@itcitysoft.com',
              reviewedAt: new Date().toISOString(),
              decisionReason: `Asked for: ${formatted}.`,
            };
            if (message.trim()) {
              next = addTimelineEntry(next, 'reply', `Sent to applicant: ${message.trim()}`);
            }
            return addTimelineEntry(
              next,
              'status_change',
              `Status set to more_info_needed. Asked for: ${formatted}.`,
            );
          });
        }}
      />
    );
  }

  /* Main drawer */
  return (
    <>
      <Drawer
        open
        onClose={onClose}
        title={
          <span className="flex items-center gap-2 min-w-0">
            <span className="truncate">{application.orgName}</span>
            <Badge tone={STATUS_TONE[application.status]}>{STATUS_LABEL[application.status]}</Badge>
          </span>
        }
        width="lg"
        footer={
          isActionable ? (
            <>
              <Button variant="ghost" onClick={onClose}>
                Close
              </Button>
              {application.status === 'pending' && (
                <Button variant="secondary" iconLeft={<Eye className="w-4 h-4" />} onClick={markInReview}>
                  Mark as in review
                </Button>
              )}
              <Button
                variant="secondary"
                iconLeft={<MessageSquare className="w-4 h-4" />}
                onClick={() => setFlow('request_info')}
              >
                Request more info
              </Button>
              <Button
                variant="destructive"
                iconLeft={<ThumbsDown className="w-4 h-4" />}
                onClick={() => setFlow('reject')}
              >
                Reject
              </Button>
              <Button iconLeft={<ThumbsUp className="w-4 h-4" />} onClick={() => setFlow('approve')}>
                Approve
              </Button>
            </>
          ) : (
            <>
              <Button variant="ghost" onClick={onClose}>
                Close
              </Button>
              {application.status === 'rejected' && (
                <Button
                  variant="secondary"
                  iconLeft={<Sparkles className="w-4 h-4" />}
                  onClick={() => setReverseConfirm(true)}
                >
                  Reverse decision
                </Button>
              )}
            </>
          )
        }
      >
        <div className="space-y-6">
          {/* Approved — success banner */}
          {application.status === 'approved' && application.resellerId && (
            <Banner
              tone="success"
              title="Reseller created"
              action={
                <Link
                  to="/admin/resellers/$id"
                  params={{ id: application.resellerId }}
                  onClick={onClose}
                  className="inline-flex items-center gap-1 text-sm font-medium hover:underline"
                >
                  Open reseller <ArrowUpRight className="w-3.5 h-3.5" />
                </Link>
              }
            >
              {application.orgName}{' '}
              <span className="mono text-xs opacity-75">({application.resellerId})</span> is now active.
            </Banner>
          )}

          {/* Rejected — reapply countdown + reason */}
          {application.status === 'rejected' && (
            <Banner tone="error" title="Application rejected">
              <div>
                {application.decisionReason ?? 'No reason recorded.'}
                {application.reapplyAfter && (
                  <div className="mt-1 text-xs opacity-90">
                    Applicant can re-apply on{' '}
                    <span className="font-semibold tabular-nums">
                      {formatDate(application.reapplyAfter)}
                    </span>{' '}
                    ({daysUntil(application.reapplyAfter)} days from today).
                  </div>
                )}
              </div>
            </Banner>
          )}

          {/* Withdrawn */}
          {application.status === 'withdrawn' && (
            <Banner tone="warning" title="Withdrawn by applicant">
              The applicant pulled this application before review. No further action required.
            </Banner>
          )}

          {/* Header (applicant + status) */}
          <section className="flex flex-col sm:flex-row sm:items-center gap-4 pb-5 border-b border-zinc-200 dark:border-zinc-700">
            <Avatar name={application.applicantName} size="xl" />
            <div className="min-w-0">
              <div className="text-base font-semibold text-zinc-950 dark:text-zinc-50">
                {application.applicantName}
              </div>
              <div className="mt-1 grid sm:grid-cols-1 gap-1 text-sm text-zinc-600 dark:text-zinc-300">
                <span className="inline-flex items-center gap-1.5">
                  <Mail className="w-3.5 h-3.5 text-zinc-400" />
                  <span className="mono text-xs">{application.email}</span>
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <Phone className="w-3.5 h-3.5 text-zinc-400" />
                  <span className="mono text-xs">{application.phone}</span>
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <MapPin className="w-3.5 h-3.5 text-zinc-400" />
                  {countryName(application.country)}
                </span>
              </div>
              <div className="mt-2 text-xs text-zinc-500 dark:text-zinc-400 tabular-nums">
                Submitted {timeAgo(application.submittedAt)} · {formatDate(application.submittedAt)}
                {application.reviewedAt && (
                  <span>
                    {' '}
                    · Last updated {timeAgo(application.reviewedAt)}{' '}
                    {application.reviewedBy && (
                      <>
                        by <span className="mono">{application.reviewedBy}</span>
                      </>
                    )}
                  </span>
                )}
              </div>
            </div>
          </section>

          {/* Organization */}
          <Section title="Organization">
            <DefList>
              <DefRow label="Org name" value={application.orgName} />
              {application.orgWebsite && (
                <DefRow
                  label="Website"
                  value={
                    <a
                      href={application.orgWebsite}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-brand dark:text-brand-dm hover:underline mono text-xs break-all"
                    >
                      {application.orgWebsite}
                      <ArrowUpRight className="w-3 h-3 flex-shrink-0" />
                    </a>
                  }
                />
              )}
              <DefRow
                label="Years in business"
                value={
                  <span className="tabular-nums">
                    {application.yearsInBusiness} {application.yearsInBusiness === 1 ? 'year' : 'years'}
                  </span>
                }
              />
              <DefRow
                label="Expected volume"
                value={
                  <span className="tabular-nums">
                    <Money minor={application.expectedVolumeMinor} hideCents tone="neutral" /> /month
                  </span>
                }
              />
            </DefList>
          </Section>

          {/* Why us */}
          <Section title="Why us">
            <blockquote className="border-l-2 border-brand dark:border-brand-dm pl-4 py-1 text-sm text-zinc-700 dark:text-zinc-200 italic">
              &ldquo;{application.whyUs}&rdquo;
            </blockquote>
          </Section>

          {/* References */}
          {application.references && (
            <Section title="References">
              <p className="text-sm text-zinc-700 dark:text-zinc-200 whitespace-pre-wrap">
                {application.references}
              </p>
            </Section>
          )}

          {/* Internal notes */}
          <Section
            title={
              <span className="inline-flex items-center gap-2">
                Internal notes
                <span className="text-[10px] font-semibold uppercase tracking-wider text-rose-600 dark:text-rose-400 bg-rose-50 dark:bg-rose-950/40 px-1.5 py-px rounded">
                  Admin only
                </span>
              </span>
            }
          >
            {application.internalNotes ? (
              <div className="rounded-md border border-amber-200 dark:border-amber-900 bg-amber-50/50 dark:bg-amber-950/20 p-3 text-sm text-amber-900 dark:text-amber-100 whitespace-pre-wrap">
                {application.internalNotes}
              </div>
            ) : (
              <p className="text-sm text-zinc-500 dark:text-zinc-400 italic">No internal notes yet.</p>
            )}
            <div className="mt-3">
              <textarea
                value={internalNoteDraft}
                onChange={(e) => setInternalNoteDraft(e.target.value)}
                placeholder="Add an internal note… (only visible to platform admins)"
                rows={3}
                className="block w-full px-3 py-2.5 text-sm rounded-md bg-white dark:bg-zinc-900 text-zinc-950 dark:text-zinc-50 border border-zinc-300 dark:border-zinc-700 placeholder-zinc-400 dark:placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-brand dark:focus:ring-brand-dm focus:border-brand dark:focus:border-brand-dm transition"
              />
              <div className="mt-2 flex items-center justify-between gap-3">
                <span className="text-xs text-zinc-500 dark:text-zinc-400">
                  {noteSaved ? (
                    <span className="text-emerald-600 dark:text-emerald-400 inline-flex items-center gap-1">
                      <CheckCircle2 className="w-3.5 h-3.5" /> Note saved
                    </span>
                  ) : (
                    'Notes appear in the timeline below.'
                  )}
                </span>
                <Button
                  variant="secondary"
                  size="sm"
                  iconLeft={<FileText className="w-3.5 h-3.5" />}
                  onClick={saveInternalNote}
                  disabled={internalNoteDraft.trim().length === 0}
                >
                  Save note
                </Button>
              </div>
            </div>
          </Section>

          {/* Timeline */}
          <Section title="Timeline">
            <Timeline entries={application.timeline} />
          </Section>
        </div>
      </Drawer>

      {/* Reverse-decision confirm */}
      <Modal
        open={reverseConfirm}
        onClose={() => setReverseConfirm(false)}
        title="Reverse decision?"
        size="sm"
        footer={
          <>
            <Button variant="ghost" onClick={() => setReverseConfirm(false)}>
              Cancel
            </Button>
            <Button variant="destructive" iconLeft={<Sparkles className="w-4 h-4" />} onClick={reverseDecision}>
              Reverse and re-open
            </Button>
          </>
        }
      >
        <p className="text-sm text-zinc-700 dark:text-zinc-200">
          This will move <span className="font-medium">{application.orgName}</span> back to{' '}
          <span className="mono">in_review</span> and clear the rejection reason. The applicant will{' '}
          <span className="font-semibold">not</span> be notified automatically.
        </p>
      </Modal>
    </>
  );
}

/* ──────────────────────────────────────────────────────────────
 * Sub-drawers (decision flows)
 * ────────────────────────────────────────────────────────────── */

function ApproveDrawer({
  application,
  onBack,
  onClose,
  onApproved,
}: {
  application: ResellerApplication;
  onBack: () => void;
  onClose: () => void;
  onApproved: (commissionPct: number, sendInvite: boolean, note: string) => void;
}) {
  const [commission, setCommission] = useState('20');
  const [note, setNote] = useState('');
  const [sendInvite, setSendInvite] = useState(true);
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);

  const commissionPct = Math.max(5, Math.min(50, Number(commission) || 0));

  function submit() {
    setSaving(true);
    setTimeout(() => {
      onApproved(commissionPct, sendInvite, note);
      setSaving(false);
      setDone(true);
    }, 700);
  }

  if (done) {
    return (
      <Drawer
        open
        onClose={onClose}
        title={
          <span className="inline-flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
            Approved
          </span>
        }
        width="lg"
        footer={
          <>
            <Button variant="ghost" onClick={onClose}>
              Close
            </Button>
            <Link to="/admin/resellers" onClick={onClose}>
              <Button iconRight={<ArrowUpRight className="w-4 h-4" />}>Open new reseller</Button>
            </Link>
          </>
        }
      >
        <div className="py-2">
          <Banner tone="success" title={`${application.orgName} approved`}>
            {sendInvite
              ? 'Invite email sent — applicant can finish setup and start onboarding businesses.'
              : 'No invite sent. You can send the invite later from the reseller profile.'}
          </Banner>
          <div className="mt-5 grid grid-cols-2 gap-4 text-sm">
            <Stat label="Initial commission" value={`${commissionPct}%`} />
            <Stat label="Owner" value={application.email} mono />
          </div>
        </div>
      </Drawer>
    );
  }

  return (
    <Drawer
      open
      onClose={onClose}
      title={
        <span className="inline-flex items-center gap-2">
          <ThumbsUp className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
          Approve application
        </span>
      }
      width="lg"
      footer={
        <>
          <Button variant="ghost" onClick={onBack} disabled={saving}>
            Back
          </Button>
          <Button onClick={submit} loading={saving} iconLeft={<ThumbsUp className="w-4 h-4" />}>
            Approve
          </Button>
        </>
      }
    >
      <div className="space-y-5">
        <Banner tone="info">
          You&rsquo;re approving <span className="font-semibold">{application.orgName}</span>{' '}
          (<span className="mono text-xs">{application.email}</span>) as a reseller. A new reseller account will be
          created and the owner will be invited to set their password.
        </Banner>

        <Input
          label="Initial commission"
          type="number"
          min={5}
          max={50}
          step={1}
          value={commission}
          onChange={(e) => setCommission(e.target.value)}
          suffix={<span className="text-xs">%</span>}
          hint="You can change this later in Reseller settings."
          required
        />

        <div>
          <span className="block text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-1.5">
            Internal note <span className="text-zinc-500 dark:text-zinc-400 font-normal">(optional)</span>
          </span>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={3}
            placeholder="Why this approval? Special arrangement?"
            className="block w-full px-3 py-2.5 text-sm rounded-md bg-white dark:bg-zinc-900 text-zinc-950 dark:text-zinc-50 border border-zinc-300 dark:border-zinc-700 placeholder-zinc-400 dark:placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-brand dark:focus:ring-brand-dm transition"
          />
        </div>

        <div className="rounded-md border border-zinc-200 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-800/40 p-4">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={sendInvite}
              onChange={(e) => setSendInvite(e.target.checked)}
              className="mt-0.5 rounded border-zinc-300 dark:border-zinc-600 text-brand focus:ring-brand"
            />
            <span className="text-sm">
              <span className="font-medium text-zinc-900 dark:text-zinc-100">
                Send invite email immediately
              </span>
              <span className="block mt-0.5 text-xs text-zinc-500 dark:text-zinc-400">
                {sendInvite
                  ? `Applicant gets a password-reset link to ${application.email}.`
                  : 'You can send the invite later from the reseller profile.'}
              </span>
            </span>
          </label>
        </div>
      </div>
    </Drawer>
  );
}

const REJECT_REASONS = [
  { v: 'insufficient', l: 'Insufficient information' },
  { v: 'service_area', l: 'Outside service area' },
  { v: 'conflict', l: 'Conflict of interest' },
  { v: 'non_serious', l: 'Suspected non-serious' },
  { v: 'other', l: 'Other' },
] as const;

function RejectDrawer({
  application,
  onBack,
  onClose,
  onRejected,
}: {
  application: ResellerApplication;
  onBack: () => void;
  onClose: () => void;
  onRejected: (reason: string, message: string, reapply: '30' | '90' | 'none') => void;
}) {
  const [reason, setReason] = useState<(typeof REJECT_REASONS)[number]['v']>('insufficient');
  const [otherDetail, setOtherDetail] = useState('');
  const [message, setMessage] = useState('');
  const [reapply, setReapply] = useState<'30' | '90' | 'none'>('30');
  const [confirmName, setConfirmName] = useState('');
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);

  const otherInvalid = reason === 'other' && otherDetail.trim().length === 0;
  const nameMismatch = confirmName.trim() !== application.orgName;
  const canSubmit = !otherInvalid && !nameMismatch;

  function submit() {
    if (!canSubmit) return;
    setSaving(true);
    setTimeout(() => {
      const reasonLabel = REJECT_REASONS.find((r) => r.v === reason)?.l ?? reason;
      const fullReason = reason === 'other' ? `Other — ${otherDetail.trim()}` : reasonLabel;
      onRejected(fullReason, message, reapply);
      setSaving(false);
      setDone(true);
    }, 700);
  }

  if (done) {
    return (
      <Drawer
        open
        onClose={onClose}
        title={
          <span className="inline-flex items-center gap-2">
            <XCircle className="w-5 h-5 text-rose-600 dark:text-rose-400" />
            Rejected
          </span>
        }
        width="lg"
        footer={
          <Button variant="ghost" onClick={onClose}>
            Close
          </Button>
        }
      >
        <Banner tone="error" title={`${application.orgName} application rejected`}>
          {reapply === 'none'
            ? 'Applicant cannot re-apply.'
            : `Applicant may re-apply after ${reapply} days.`}
          {message.trim() && (
            <div className="mt-2 text-xs opacity-90">Rejection email queued for {application.email}.</div>
          )}
        </Banner>
      </Drawer>
    );
  }

  return (
    <Drawer
      open
      onClose={onClose}
      title={
        <span className="inline-flex items-center gap-2">
          <ThumbsDown className="w-5 h-5 text-rose-600 dark:text-rose-400" />
          Reject application
        </span>
      }
      width="lg"
      footer={
        <>
          <Button variant="ghost" onClick={onBack} disabled={saving}>
            Back
          </Button>
          <Button
            variant="destructive"
            onClick={submit}
            loading={saving}
            disabled={!canSubmit}
            iconLeft={<ThumbsDown className="w-4 h-4" />}
          >
            Reject application
          </Button>
        </>
      }
    >
      <div className="space-y-5">
        <Banner tone="warning" title="This decision is recorded permanently">
          You can reverse a rejection later, but the timeline keeps the original entry.
        </Banner>

        <div>
          <span className="block text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-2">Reason</span>
          <div className="space-y-2">
            {REJECT_REASONS.map((r) => (
              <Radio
                key={r.v}
                name="reject-reason"
                checked={reason === r.v}
                onChange={() => setReason(r.v)}
                label={r.l}
              />
            ))}
          </div>
          {reason === 'other' && (
            <div className="mt-3">
              <textarea
                value={otherDetail}
                onChange={(e) => setOtherDetail(e.target.value)}
                rows={3}
                placeholder="Please describe the reason… (required)"
                className={cn(
                  'block w-full px-3 py-2.5 text-sm rounded-md bg-white dark:bg-zinc-900 text-zinc-950 dark:text-zinc-50 border placeholder-zinc-400 dark:placeholder-zinc-500 focus:outline-none focus:ring-2 transition',
                  otherInvalid
                    ? 'border-rose-500 focus:ring-rose-500'
                    : 'border-zinc-300 dark:border-zinc-700 focus:ring-brand dark:focus:ring-brand-dm',
                )}
              />
              {otherInvalid && (
                <span className="block text-xs text-rose-600 dark:text-rose-400 mt-1">
                  Please describe the reason for rejection.
                </span>
              )}
            </div>
          )}
        </div>

        <div>
          <span className="block text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-1.5">
            Message to applicant <span className="text-zinc-500 dark:text-zinc-400 font-normal">(optional)</span>
          </span>
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows={4}
            placeholder="A short note the applicant will see in their email…"
            className="block w-full px-3 py-2.5 text-sm rounded-md bg-white dark:bg-zinc-900 text-zinc-950 dark:text-zinc-50 border border-zinc-300 dark:border-zinc-700 placeholder-zinc-400 dark:placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-brand dark:focus:ring-brand-dm transition"
          />
          <span className="block text-xs text-zinc-500 dark:text-zinc-400 mt-1.5">
            We&rsquo;ll include this verbatim in the rejection email.
          </span>
        </div>

        <div>
          <span className="block text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-2">Re-apply policy</span>
          <div className="space-y-2">
            <Radio
              name="reapply"
              checked={reapply === '30'}
              onChange={() => setReapply('30')}
              label="Allow re-apply after 30 days"
            />
            <Radio
              name="reapply"
              checked={reapply === '90'}
              onChange={() => setReapply('90')}
              label="Allow re-apply after 90 days"
            />
            <Radio
              name="reapply"
              checked={reapply === 'none'}
              onChange={() => setReapply('none')}
              label="No re-apply"
            />
          </div>
        </div>

        <div className="rounded-md border border-rose-200 dark:border-rose-900 bg-rose-50/50 dark:bg-rose-950/20 p-4">
          <label className="flex items-start gap-2.5">
            <ShieldAlert className="w-4 h-4 text-rose-600 dark:text-rose-400 mt-0.5 flex-shrink-0" />
            <span className="text-sm">
              <span className="block font-medium text-rose-900 dark:text-rose-100 mb-2">
                Confirm by typing the org name
              </span>
              <input
                type="text"
                value={confirmName}
                onChange={(e) => setConfirmName(e.target.value)}
                placeholder={application.orgName}
                className={cn(
                  'block w-full px-3 py-2 text-sm rounded-md bg-white dark:bg-zinc-900 text-zinc-950 dark:text-zinc-50 border placeholder-zinc-400 dark:placeholder-zinc-500 focus:outline-none focus:ring-2 transition mono',
                  nameMismatch && confirmName.length > 0
                    ? 'border-rose-500 focus:ring-rose-500'
                    : 'border-zinc-300 dark:border-zinc-700 focus:ring-rose-500',
                )}
              />
            </span>
          </label>
        </div>
      </div>
    </Drawer>
  );
}

const INFO_ASKS = [
  { v: 'company_reg', l: 'Company registration document' },
  { v: 'reference_letter', l: 'Customer reference letter' },
  { v: 'portfolio', l: 'Portfolio details' },
  { v: 'payment_method', l: 'Payment-method readiness' },
  { v: 'other', l: 'Other' },
] as const;

function RequestInfoDrawer({
  application,
  onBack,
  onClose,
  onSent,
}: {
  application: ResellerApplication;
  onBack: () => void;
  onClose: () => void;
  onSent: (asks: string[], message: string) => void;
}) {
  const [checked, setChecked] = useState<Record<string, boolean>>({});
  const [otherDetail, setOtherDetail] = useState('');
  const [message, setMessage] = useState('');
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);

  function toggle(v: string) {
    setChecked((c) => ({ ...c, [v]: !c[v] }));
  }

  const otherInvalid = checked.other && otherDetail.trim().length === 0;
  const anyChecked = Object.values(checked).some(Boolean);
  const canSubmit = anyChecked && !otherInvalid;

  function submit() {
    if (!canSubmit) return;
    setSaving(true);
    setTimeout(() => {
      const asks = INFO_ASKS.flatMap((a) => {
        if (!checked[a.v]) return [];
        if (a.v === 'other') return [`Other — ${otherDetail.trim()}`];
        return [a.l];
      });
      onSent(asks, message);
      setSaving(false);
      setDone(true);
    }, 700);
  }

  if (done) {
    return (
      <Drawer
        open
        onClose={onClose}
        title={
          <span className="inline-flex items-center gap-2">
            <ClipboardCheck className="w-5 h-5 text-amber-600 dark:text-amber-400" />
            Information requested
          </span>
        }
        width="lg"
        footer={
          <Button variant="ghost" onClick={onClose}>
            Close
          </Button>
        }
      >
        <Banner tone="success" title="Request sent">
          Email queued for <span className="mono">{application.email}</span>. Application status moved to{' '}
          <span className="font-semibold">Needs info</span>.
        </Banner>
      </Drawer>
    );
  }

  return (
    <Drawer
      open
      onClose={onClose}
      title={
        <span className="inline-flex items-center gap-2">
          <MessageSquare className="w-5 h-5 text-amber-600 dark:text-amber-400" />
          Request more info
        </span>
      }
      width="lg"
      footer={
        <>
          <Button variant="ghost" onClick={onBack} disabled={saving}>
            Back
          </Button>
          <Button
            onClick={submit}
            loading={saving}
            disabled={!canSubmit}
            iconLeft={<Send className="w-4 h-4" />}
          >
            Send request
          </Button>
        </>
      }
    >
      <div className="space-y-5">
        <Banner tone="info">
          The applicant will receive an email listing the items below. Their application moves to{' '}
          <span className="mono">more_info_needed</span> until they reply.
        </Banner>

        <div>
          <span className="block text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-2">
            What do you need?
          </span>
          <div className="space-y-2">
            {INFO_ASKS.map((a) => (
              <label key={a.v} className="flex items-center gap-2.5 text-sm text-zinc-700 dark:text-zinc-200 cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!checked[a.v]}
                  onChange={() => toggle(a.v)}
                  className="rounded border-zinc-300 dark:border-zinc-600 text-brand focus:ring-brand"
                />
                {a.l}
              </label>
            ))}
          </div>
          {checked.other && (
            <div className="mt-3 ml-6">
              <input
                type="text"
                value={otherDetail}
                onChange={(e) => setOtherDetail(e.target.value)}
                placeholder="Describe what you need…"
                className={cn(
                  'block w-full px-3 py-2 text-sm rounded-md bg-white dark:bg-zinc-900 text-zinc-950 dark:text-zinc-50 border placeholder-zinc-400 dark:placeholder-zinc-500 focus:outline-none focus:ring-2 transition',
                  otherInvalid
                    ? 'border-rose-500 focus:ring-rose-500'
                    : 'border-zinc-300 dark:border-zinc-700 focus:ring-brand dark:focus:ring-brand-dm',
                )}
              />
              {otherInvalid && (
                <span className="block text-xs text-rose-600 dark:text-rose-400 mt-1">
                  Please describe what you need.
                </span>
              )}
            </div>
          )}
          {!anyChecked && (
            <span className="block text-xs text-zinc-500 dark:text-zinc-400 mt-2">
              Pick at least one item to request.
            </span>
          )}
        </div>

        <div>
          <span className="block text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-1.5">
            Message to applicant
          </span>
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows={4}
            placeholder="Add context — when you need it by, where to upload, etc."
            className="block w-full px-3 py-2.5 text-sm rounded-md bg-white dark:bg-zinc-900 text-zinc-950 dark:text-zinc-50 border border-zinc-300 dark:border-zinc-700 placeholder-zinc-400 dark:placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-brand dark:focus:ring-brand-dm transition"
          />
        </div>
      </div>
    </Drawer>
  );
}

/* ──────────────────────────────────────────────────────────────
 * Small layout helpers
 * ────────────────────────────────────────────────────────────── */

function Section({ title, children }: { title: React.ReactNode; children: React.ReactNode }) {
  return (
    <section>
      <h3 className="text-xs font-semibold uppercase tracking-wider text-zinc-500 dark:text-zinc-400 mb-2">
        {title}
      </h3>
      {children}
    </section>
  );
}

function DefList({ children }: { children: React.ReactNode }) {
  return (
    <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2 text-sm">{children}</dl>
  );
}

function DefRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[7rem_1fr] gap-3 items-start">
      <dt className="text-xs uppercase tracking-wider font-medium text-zinc-500 dark:text-zinc-400 pt-0.5">
        {label}
      </dt>
      <dd className="text-zinc-900 dark:text-zinc-100">{value}</dd>
    </div>
  );
}

function Stat({ label, value, mono = false }: { label: string; value: React.ReactNode; mono?: boolean }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wider font-semibold text-zinc-500 dark:text-zinc-400 mb-0.5">
        {label}
      </div>
      <div className={cn('text-zinc-900 dark:text-zinc-100', mono && 'mono text-xs')}>{value}</div>
    </div>
  );
}

function Radio({
  name,
  checked,
  onChange,
  label,
}: {
  name: string;
  checked: boolean;
  onChange: () => void;
  label: string;
}) {
  return (
    <label className="flex items-center gap-2.5 text-sm text-zinc-700 dark:text-zinc-200 cursor-pointer">
      <input
        type="radio"
        name={name}
        checked={checked}
        onChange={onChange}
        className="text-brand focus:ring-brand"
      />
      {label}
    </label>
  );
}

function Timeline({ entries }: { entries: ResellerApplication['timeline'] }) {
  if (entries.length === 0) {
    return <p className="text-sm text-zinc-500 dark:text-zinc-400 italic">No events yet.</p>;
  }
  return (
    <ol className="relative space-y-4 pl-6 before:absolute before:left-2 before:top-1.5 before:bottom-1.5 before:w-px before:bg-zinc-200 dark:before:bg-zinc-700">
      {entries.map((e, i) => (
        <li key={i} className="relative">
          <span
            className={cn(
              'absolute -left-[19px] top-1 w-3 h-3 rounded-full border-2 border-white dark:border-zinc-900',
              KIND_DOT[e.kind] ?? 'bg-zinc-400',
            )}
          />
          <div className="text-sm text-zinc-900 dark:text-zinc-100">{e.text}</div>
          <div className="text-xs text-zinc-500 dark:text-zinc-400 mt-0.5 tabular-nums">
            <span className="mono">{e.by}</span> · {timeAgo(e.at)} ({formatDate(e.at)})
          </div>
        </li>
      ))}
    </ol>
  );
}

const KIND_DOT: Record<ResellerApplication['timeline'][number]['kind'], string> = {
  submitted: 'bg-sky-500',
  status_change: 'bg-emerald-500',
  note: 'bg-amber-500',
  reply: 'bg-brand dark:bg-brand-dm',
};

function daysUntil(date: string): number {
  const ms = new Date(date).getTime() - Date.now();
  return Math.max(0, Math.ceil(ms / 86400000));
}
